import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import analyticsService from '../../services/analyticsService';
import DualLineChart from '../../components/charts/DualLineChart';
import TrendBarChart from '../../components/charts/TrendBarChart';
import MarkdownMessage from '../../components/MarkdownMessage';
import SalesImportModal from '../../components/SalesImportModal';

const toISODate = (d) => d.toISOString().split('T')[0];

const RANGE_PRESETS = {
  ytd: () => {
    const now = new Date();
    return { startDate: `${now.getFullYear()}-01-01`, endDate: toISODate(now) };
  },
  thisMonth: () => {
    const now = new Date();
    return { startDate: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: toISODate(now) };
  },
  lastMonth: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { startDate: toISODate(start), endDate: toISODate(end) };
  },
};

const fmtMoney = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const fmtNum = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const momArrow = (v) => (v == null ? '' : v > 0 ? '▲' : v < 0 ? '▼' : '•');
const momClass = (v) => (v == null ? 'text-muted' : v > 0 ? 'text-success' : v < 0 ? 'text-danger' : 'text-muted');

function TooltipLabel({ label, title }) {
  return (
    <span>
      {label}{' '}
      <span title={title} style={{ cursor: 'help', color: '#adb5bd', fontWeight: 400 }}>ⓘ</span>
    </span>
  );
}

function KpiCard({ icon, color, bg, label, value, sub }) {
  return (
    <div className="col-12 col-md-3 d-flex">
      <div className="card flex-fill" style={{ borderTop: `3px solid ${color}`, minHeight: 132, border: '1px solid #e9ecef', borderTopWidth: 3, borderTopColor: color }}>
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center gap-2 mb-1">
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8, background: bg, fontSize: 16, flexShrink: 0,
              }}
            >
              {icon}
            </span>
            <span className="text-muted" style={{ fontSize: 13 }}>{label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
          {sub && <div className="text-muted mt-auto pt-1" style={{ fontSize: 11.5 }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function SortableHeader({ label, title, sortKey, sort, onSort, className = 'text-end' }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
    >
      {title ? <TooltipLabel label={label} title={title} /> : label}
      <span style={{ marginLeft: 4, color: active ? '#1b2638' : '#ced4da', fontSize: 10 }}>
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

function AskAiPanel() {
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const historyRef = useRef([]); // the raw {role, content} pairs the backend actually sent/received
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const question = input.trim();
    if (!question || sending) return;
    setError('');
    setMessages(prev => [...prev, { role: 'user', content: question, displayOnly: true }]);
    setInput('');
    setSending(true);
    try {
      const result = await analyticsService.askSalesAi(question, historyRef.current);
      historyRef.current = result.history;
      setMessages(prev => [...prev, { role: 'assistant', content: result.answer }]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to get an answer.');
      // Roll back the optimistic user bubble's effect on history — history itself was never
      // advanced since the call failed, so just leave the error visible.
    } finally {
      setSending(false);
    }
  };

  const newChat = () => {
    setMessages([]);
    historyRef.current = [];
    setError('');
  };

  const SUGGESTIONS = [
    'Am I selling my product well?',
    'Is the business making money?',
    'Which customer is my most profitable?',
    'How much is my business worth?',
  ];

  return (
    <div className="card mt-3">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">💬 Ask AI About Your Business</h5>
          {messages.length > 0 && (
            <button className="btn btn-sm btn-outline-secondary" onClick={newChat}>New Chat</button>
          )}
        </div>

        {messages.length === 0 && (
          <div className="mb-3">
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
              Ask anything about your sales, margins, or customers — grounded in the real numbers above.
            </div>
            <div className="d-flex flex-wrap gap-2">
              {SUGGESTIONS.map(q => (
                <button key={q} className="btn btn-sm btn-outline-primary" onClick={() => setInput(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.length > 0 && (
          <div style={{ maxHeight: 420, overflowY: 'auto' }} className="mb-3 p-2 border rounded bg-light">
            {messages.map((m, i) => (
              <div key={i} className={`d-flex mb-2 ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                <div
                  className={`p-2 rounded ${m.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                  style={{ maxWidth: '85%', fontSize: 14, whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal' }}
                >
                  {m.role === 'user' ? m.content : <MarkdownMessage content={m.content} />}
                </div>
              </div>
            ))}
            {sending && (
              <div className="d-flex justify-content-start mb-2">
                <div className="p-2 rounded bg-white border text-muted" style={{ fontSize: 13 }}>
                  <span className="spinner-border spinner-border-sm me-2" />Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}

        {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Ask a question…"
            value={input}
            disabled={sending}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
          />
          <button className="btn btn-primary" onClick={send} disabled={sending || !input.trim()}>
            {sending ? <span className="spinner-border spinner-border-sm" /> : 'Send'}
          </button>
        </div>
        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
          Answers are grounded in your real sales data, but any valuation-style question gets a rough estimate only — not a real appraisal.
        </div>
      </div>
    </div>
  );
}

export default function SalesTrendsPage() {
  const [preset, setPreset] = useState('ytd');
  const [range, setRange] = useState(() => RANGE_PRESETS.ytd());
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });
  const [showImport, setShowImport] = useState(false);

  const applyPreset = (key) => {
    setPreset(key);
    if (RANGE_PRESETS[key]) setRange(RANGE_PRESETS[key]());
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        analyticsService.getSalesSummary(range),
        analyticsService.getSalesByCustomer(range),
      ]);
      setSummary(s);
      setCustomers(c.customers || []);
    } catch (e) {
      console.error(e);
      setSummary(null);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = summary?.kpis ?? {};

  const revenuePoints = useMemo(() => (summary?.charts?.monthly ?? []).map(m => ({ x: m.month, y: m.revenue })), [summary]);
  const marginPoints = useMemo(() => (summary?.charts?.monthly ?? []).map(m => ({ x: m.month, y: m.margin })), [summary]);

  const topCustomers = useMemo(() => customers.slice(0, 10), [customers]);

  const handleSort = (key) => {
    setSort(prev => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // Sensible first-click direction per column: names A-Z, numbers biggest-first.
      return { key, dir: key === 'customerName' ? 'asc' : 'desc' };
    });
  };

  const sortedCustomers = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...customers].sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      if (sort.key === 'customerName') {
        return dir * String(av || '').localeCompare(String(bv || ''));
      }
      // nulls (e.g. no MoM data) always sort last regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir * (Number(av) - Number(bv));
    });
  }, [customers, sort]);

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h4 className="mb-0">Sales & Margin</h4>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => setShowImport(true)}>
              📥 Import from QuickBooks
            </button>
            <button className="btn btn-outline-primary" onClick={fetchData} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Date range */}
        <div className="row mt-3 g-2 align-items-end">
          <div className="col-auto">
            <div className="btn-group" role="group">
              {[['ytd', 'YTD'], ['thisMonth', 'This Month'], ['lastMonth', 'Last Month'], ['custom', 'Custom']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm ${preset === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => applyPreset(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="col-12 col-sm-3">
            <label className="form-label mb-1" style={{ fontSize: 12 }}>Start Date</label>
            <input
              type="date" className="form-control form-control-sm"
              value={range.startDate}
              onChange={e => { setPreset('custom'); setRange(r => ({ ...r, startDate: e.target.value })); }}
            />
          </div>
          <div className="col-12 col-sm-3">
            <label className="form-label mb-1" style={{ fontSize: 12 }}>End Date</label>
            <input
              type="date" className="form-control form-control-sm"
              value={range.endDate}
              onChange={e => { setPreset('custom'); setRange(r => ({ ...r, endDate: e.target.value })); }}
            />
          </div>
        </div>

        {/* KPI Tiles */}
        <div className="row mt-3 g-3">
          <KpiCard
            icon="💰" color="#2563eb" bg="#dbeafe"
            label="Revenue"
            value={fmtMoney(kpis.totalRevenue)}
            sub={`${kpis.orderCount ?? 0} orders · avg ${fmtMoney(kpis.avgOrderValue)}`}
          />
          <KpiCard
            icon="🧾" color="#d97706" bg="#fef3c7"
            label="COGS"
            value={fmtMoney((kpis.totalIngredientCogs || 0) + (kpis.totalPackagingCogs || 0) + (kpis.totalLaborCogs || 0))}
            sub={`Ingr. ${fmtMoney(kpis.totalIngredientCogs)} · Pkg ${fmtMoney(kpis.totalPackagingCogs)} · Labor ${fmtMoney(kpis.totalLaborCogs)}`}
          />
          <KpiCard
            icon={kpis.grossMargin < 0 ? '📉' : '📈'}
            color={kpis.grossMargin < 0 ? '#dc2626' : '#16a34a'}
            bg={kpis.grossMargin < 0 ? '#fee2e2' : '#dcfce7'}
            label="Gross Margin"
            value={fmtMoney(kpis.grossMargin)}
            sub={kpis.grossMarginPct != null ? `${kpis.grossMarginPct}%` : '—'}
          />
          <KpiCard
            icon="📦" color="#7c3aed" bg="#ede9fe"
            label="Units Sold"
            value={fmtNum(kpis.unitsSold)}
          />
        </div>

        {kpis.hasEstimatedCosts && (
          <div className="alert alert-warning mt-3 py-2" style={{ fontSize: 12 }}>
            ⚠ Some orders in this range predate exact cost tracking — their ingredient/packaging
            cost is reconstructed from today's prices, not the exact cost at the time of sale.
            Labor cost is always a period-wide average allocated per unit sold, not tracked per order.
          </div>
        )}
        {kpis.importedRowCount > 0 && (
          <div className="alert alert-info mt-3 py-2" style={{ fontSize: 12 }}>
            ℹ️ Includes {fmtMoney(kpis.importedRevenue)} from {kpis.importedRowCount} imported
            QuickBooks transaction{kpis.importedRowCount === 1 ? '' : 's'}, filling customer/month
            gaps quickLOG had no order data for.
          </div>
        )}

        {/* Chart */}
        <div className="row mt-3 g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <DualLineChart
                  title="Monthly Revenue vs. Gross Margin"
                  pointsA={revenuePoints}
                  labelA="Revenue"
                  pointsB={marginPoints}
                  labelB="Gross Margin"
                  colorA="#1b2638"
                  colorB="#27ae60"
                  formatBAsCurrency
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sales by customer */}
        <div className="row mt-3 g-3">
          <div className="col-12 col-lg-5">
            <div className="card">
              <div className="card-body">
                <TrendBarChart
                  title="Sales by Customer (top 10)"
                  labels={topCustomers.map(c => c.customerName)}
                  values={topCustomers.map(c => c.revenue)}
                  label="Revenue"
                  yAxisTitle="Revenue ($)"
                  emptyText="No sales in this range."
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-7">
            <div className="card">
              <div className="card-body">
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  <table className="table table-sm" style={{ fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        <SortableHeader label="Customer" sortKey="customerName" sort={sort} onSort={handleSort} className="" />
                        <SortableHeader label="Revenue" sortKey="revenue" sort={sort} onSort={handleSort} />
                        <SortableHeader label="GM" title="Gross Margin ($) — revenue minus ingredient, packaging, and allocated labor cost" sortKey="margin" sort={sort} onSort={handleSort} />
                        <SortableHeader label="GM %" title="Gross Margin as a % of revenue" sortKey="marginPct" sort={sort} onSort={handleSort} />
                        <SortableHeader label="MoM" title="Month-over-month revenue change vs. the prior calendar month" sortKey="momRevenueChangePct" sort={sort} onSort={handleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.length === 0 ? (
                        <tr><td colSpan={5} className="text-muted text-center py-3">No sales in this range.</td></tr>
                      ) : sortedCustomers.map(c => (
                        <tr key={c.customerId ?? 'none'}>
                          <td>{c.customerName}</td>
                          <td className="text-end">{fmtMoney(c.revenue)}</td>
                          <td className="text-end">{fmtMoney(c.margin)}</td>
                          <td className="text-end">{c.marginPct != null ? `${c.marginPct}%` : '—'}</td>
                          <td className={`text-end ${momClass(c.momRevenueChangePct)}`}>
                            {momArrow(c.momRevenueChangePct)} {fmtPct(c.momRevenueChangePct)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AskAiPanel />
      </div>
      {showImport && (
        <SalesImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchData(); }}
        />
      )}
    </div>
  );
}
